/**
 * useTabPresence — Slice 38 (2026-05-02).
 *
 * Detects whether another browser tab has the same application open
 * via a BroadcastChannel keyed on applicationId. The cross-tab sync
 * path was disabled in commit ee742b3 because it raced
 * catastrophically with the 500ms debounced auto-save (a remote
 * snapshot arrived mid-edit and reset the engine to the older
 * persisted value).
 *
 * Without that sync, two tabs editing the same application is a
 * silent data-loss path: tab A types "Alex", tab B types "Sam", the
 * last persist wins, the other's edits vanish with no warning.
 *
 * This hook does the minimum-viable mitigation: detect simultaneous
 * tabs, return a flag, let the App show a warning banner and pause
 * auto-save in non-primary tabs. Proper leader election with conflict
 * merge is a larger architectural change tracked separately.
 *
 * Algorithm:
 *
 *  - Each tab generates a fresh `tabId` on mount.
 *  - On mount, the tab broadcasts `{type: 'hello', tabId}` and starts
 *    a periodic `{type: 'still-here', tabId}` heartbeat.
 *  - When another tab's message arrives, both tabs become aware of
 *    each other. The lower `tabId` (lexicographic) wins primary
 *    status; the other becomes secondary.
 *  - On `pagehide` / unmount, broadcast `{type: 'goodbye', tabId}`
 *    so survivors can re-elect.
 *  - If no message has arrived from a known peer for >3× the
 *    heartbeat interval, presume that peer crashed and drop it.
 *
 * BroadcastChannel is supported in all modern browsers (Chrome, Edge,
 * Firefox, Safari 15.4+). When unavailable, the hook returns
 * `{ isPrimary: true, peerCount: 0 }` (single-tab assumption).
 */
import { useEffect, useRef, useState } from 'react';

interface ITabPresence {
  /** True when this tab is the elected leader (or alone). */
  isPrimary: boolean;
  /** Number of OTHER tabs currently broadcasting on this app. */
  peerCount: number;
}

interface ITabHello {
  type: 'hello' | 'still-here' | 'goodbye';
  tabId: string;
  ts: number;
}

const HEARTBEAT_MS = 2_000;
const PEER_STALE_MS = HEARTBEAT_MS * 3;

function makeTabId(): string {
  // 96 bits of entropy is plenty to avoid collisions across tabs of
  // the same browser / app load. Use crypto.randomUUID() when
  // available (Chrome 92+, Firefox 95+, Safari 15.4+); fall back to
  // a Math.random() shape for older targets.
  if (
    typeof globalThis !== 'undefined' &&
    typeof globalThis.crypto?.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }
  return `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

export function useTabPresence(applicationId: string | undefined): ITabPresence {
  const [isPrimary, setIsPrimary] = useState(true);
  const [peerCount, setPeerCount] = useState(0);
  const tabIdRef = useRef<string>('');
  if (tabIdRef.current === '') tabIdRef.current = makeTabId();

  useEffect(() => {
    if (!applicationId) return undefined;
    if (typeof BroadcastChannel === 'undefined') return undefined;

    const channelName = `inside-us-aos.tab-presence.${applicationId}`;
    const channel = new BroadcastChannel(channelName);
    const myId = tabIdRef.current;
    const peers = new Map<string, number>(); // tabId → last-seen ts

    const recompute = (): void => {
      const now = Date.now();
      for (const [pid, ts] of peers) {
        if (now - ts > PEER_STALE_MS) peers.delete(pid);
      }
      setPeerCount(peers.size);
      // Lower tabId wins primary; alone wins by default.
      let primary = true;
      for (const pid of peers.keys()) {
        if (pid < myId) {
          primary = false;
          break;
        }
      }
      setIsPrimary(primary);
    };

    const post = (type: ITabHello['type']): void => {
      try {
        channel.postMessage({ type, tabId: myId, ts: Date.now() });
      } catch {
        // Channel closed mid-tick — fine, next heartbeat will retry.
      }
    };

    channel.onmessage = (ev: MessageEvent<ITabHello>) => {
      const msg = ev.data;
      if (msg.tabId === myId) return; // ignore echoes
      if (msg.type === 'goodbye') {
        peers.delete(msg.tabId);
        recompute();
        return;
      }
      // hello / still-here both register presence.
      peers.set(msg.tabId, msg.ts);
      // On hello from a fresh tab, reply with our presence so they
      // see us right away (don't wait for our heartbeat).
      if (msg.type === 'hello') post('still-here');
      recompute();
    };

    post('hello');
    const heartbeat = setInterval(() => {
      post('still-here');
      recompute();
    }, HEARTBEAT_MS);

    const onPageHide = (): void => post('goodbye');
    window.addEventListener('pagehide', onPageHide);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('pagehide', onPageHide);
      post('goodbye');
      channel.close();
    };
  }, [applicationId]);

  return { isPrimary, peerCount };
}
